import React, {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {
  HeaderTitle,
  Divider,
  Tab,
  TabsNavigation,
  Button
} from '@/components/atoms';
import LeaderboardTable from './LeaderboardTable';
import LeaderboardUploadModal from './LeaderboardUploadModal';
import {downloadTemplateFile} from '@/services/downloadTemplateFile';
import {getSasToken} from '@/services/sasToken';
import {downloadCSVFiles, getDateWithTimeString} from '@/helpers/shadcn/utils';
import {logger} from '@/helpers/logger';
import {getFormattedDate} from '@/utils/commons';
import {formatLeaderboardLastUpdate} from '@/containers/Activity/leaderboardDateUtils';
import {useAppDispatch} from '@/store';
import {setToastNotifications} from '@/store/slices/commonSlice';
import styles from './Leaderboard.module.scss';
import {ButtonIcon} from '@pepsico-ds/ui';
import {Constants} from '@/utils/constants/commonConstants';
import {useAzureBlob} from '@/hooks/useAzureBlob-hook';

const LEADERBOARD_PERIODS = {
  DAILY: 'Daily',
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
  YEARLY: 'Yearly'
};

const TABS = [
  {id: LEADERBOARD_PERIODS.DAILY, label: 'Daily'},
  {id: LEADERBOARD_PERIODS.WEEKLY, label: 'Weekly'},
  {id: LEADERBOARD_PERIODS.MONTHLY, label: 'Monthly'},
  {id: LEADERBOARD_PERIODS.YEARLY, label: 'Yearly'}
];

const LeaderboardSection = ({formData, handleFormChange}) => {
  const dispatch = useAppDispatch();
  const {uploadFileOnBlob} = useAzureBlob();
  const [activeTab, setActiveTab] = useState(LEADERBOARD_PERIODS.DAILY);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [leaderboardData, setLeaderboardData] = useState({
    Daily: [],
    Weekly: [],
    Monthly: [],
    Yearly: []
  });
  const [uploadedFiles, setUploadedFiles] = useState({
    Daily: null,
    Weekly: null,
    Monthly: null,
    Yearly: null
  });
  const [lastUpdate, setLastUpdate] = useState({
    Daily: null,
    Weekly: null,
    Monthly: null,
    Yearly: null
  });
  const pendingUploadRef = useRef(null);

  // Initialize from formData.leaderboards (already transformed from API)
  useEffect(() => {
    if (formData?.leaderboards && typeof formData.leaderboards === 'object') {
      const newLeaderboardData = {};
      const newUploadedFiles = {};
      const newLastUpdate = {};

      // Initialize all periods with empty data
      Object.values(LEADERBOARD_PERIODS).forEach((period) => {
        newLeaderboardData[period] = [];
        newUploadedFiles[period] = null;
        newLastUpdate[period] = null;
      });

      // Load existing leaderboard data
      Object.entries(formData.leaderboards).forEach(([period, periodData]) => {
        if (periodData?.data && Array.isArray(periodData.data)) {
          newLeaderboardData[period] = periodData.data;
          const persistedFileName =
            periodData.leaderboardFileName || periodData.fileName;
          if (persistedFileName) {
            newUploadedFiles[period] = {name: persistedFileName};
          }
          if (periodData.updatedAt) {
            newLastUpdate[period] = periodData.updatedAt;
          } else if (periodData.lastUpdate) {
            newLastUpdate[period] = periodData.lastUpdate;
          }
        }
      });

      setLeaderboardData(newLeaderboardData);
      setUploadedFiles(newUploadedFiles);
      setLastUpdate(newLastUpdate);
    }
  }, [formData?.leaderboards]);

  const handleUpload = useCallback(
    async (file, formattedData) => {
      try {
        const sasResponse = await getSasToken(file.name, Constants.Leaderboard);

        if (!sasResponse?.blobUrl) {
          throw new Error('Upload URL not available');
        }

        const uploadResponse = await uploadFileOnBlob(sasResponse, file);

        if (uploadResponse?.message !== Constants.Success) {
          throw new Error('Upload failed');
        }

        const updateDate = getFormattedDate(
          new Date().toISOString(),
          false,
          '-',
          'mm-dd-yyyy'
        );

        const uploadedFileUrl =
          uploadResponse?.media?.imageUrl || sasResponse?.blobUrl || '';
        const updatedAt = new Date().toISOString();

        // Store pending data to be applied on Done
        pendingUploadRef.current = {
          file,
          formattedData,
          updateDate,
          uploadedFileUrl,
          updatedAt
        };

        return true;
      } catch (error) {
        logger.error('Leaderboard file upload failed:', error);

        dispatch(
          setToastNotifications({
            show: true,
            message: 'Failed to upload file. Please try again.',
            type: 'error'
          })
        );

        return false;
      }
    },
    [dispatch, uploadFileOnBlob]
  );

  const handleUploadDone = useCallback(() => {
    const pending = pendingUploadRef.current;
    if (pending) {
      const {file, formattedData, updateDate, uploadedFileUrl, updatedAt} =
        pending;

      setLeaderboardData((prev) => ({
        ...prev,
        [activeTab]: formattedData
      }));

      setUploadedFiles((prev) => ({
        ...prev,
        [activeTab]: file
      }));

      setLastUpdate((prev) => ({
        ...prev,
        [activeTab]: updateDate
      }));

      handleFormChange({
        leaderboards: {
          ...formData?.leaderboards,
          [activeTab]: {
            data: formattedData,
            leaderboardFileName: file.name,
            leaderboardFileUrl: uploadedFileUrl,
            updatedAt,
            fileName: file.name,
            lastUpdate: updateDate
          }
        }
      });

      pendingUploadRef.current = null;
    }

    dispatch(
      setToastNotifications({
        show: true,
        message: 'File uploaded successfully',
        type: 'success'
      })
    );
  }, [dispatch, activeTab, handleFormChange, formData]);

  const handleFileDelete = useCallback(() => {
    pendingUploadRef.current = null;

    setUploadedFiles((prev) => ({
      ...prev,
      [activeTab]: null
    }));

    setLeaderboardData((prev) => ({
      ...prev,
      [activeTab]: []
    }));

    setLastUpdate((prev) => ({
      ...prev,
      [activeTab]: null
    }));

    handleFormChange({
      leaderboards: {
        ...formData?.leaderboards,
        [activeTab]: undefined
      }
    });
  }, [activeTab, handleFormChange, formData]);

  const handleCloseModal = useCallback(() => {
    pendingUploadRef.current = null;
    setIsModalOpen(false);
  }, []);

  const handleOpenUpload = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleRefresh = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleLeaderboardTemplateDownload = useCallback(async () => {
    const periodLabel =
      TABS.find((t) => t.id === activeTab)?.label || 'Leaderboard';
    const body = {type: `Leaderboard ${activeTab}`};
    const response = await downloadTemplateFile(body);
    if (!response) return;
    const fileName = `${periodLabel}_Leaderboard_${getDateWithTimeString()}`;
    downloadCSVFiles(response, fileName);
  }, [activeTab]);

  const currentData = leaderboardData[activeTab] || [];
  const currentFile = uploadedFiles[activeTab];
  const updatedDate = formData?.leaderboards?.[activeTab]?.updatedAt;
  const currentLastUpdate = lastUpdate[activeTab];
  const formattedLastUpdate = formatLeaderboardLastUpdate(
    updatedDate,
    currentLastUpdate
  );
  const currentLastUpdatedLabel =
    formattedLastUpdate !== '-' ? `Last Updated: ${formattedLastUpdate}` : null;
  const currentPeriod = TABS.find((t) => t.id === activeTab)?.label || 'Daily';

  return (
    <>
      <div
        className='rounded-lg bg-[#1f1f1f] shadow-[0_0_24px_rgba(0,0,0,0.08)]'
        data-testid='leaderboard-section'>
        <div className='px-4 py-4'>
          <HeaderTitle
            title='Leaderboards'
            className='text-[16px] font-bold leading-[22px] text-[#FFFFFF]'
          />
        </div>

        <Divider className='mx-3 my-0 border-0 border-b' />

        <div className='flex flex-col gap-4 px-4 py-3'>
          <TabsNavigation
            className='border-b border-[#4E4E4E]'
            activeTabIndex={TABS.findIndex((t) => t.id === activeTab)}
            handleSetActiveTab={(index) => setActiveTab(TABS[index].id)}>
            {TABS.map((tab) => (
              <Tab
                key={tab.id}
                label={tab.label}
                onClick={() => setActiveTab(tab.id)}
                isDark
              />
            ))}
          </TabsNavigation>

          <div
            className={`${currentFile ? 'w-fit max-w-[600px]' : 'max-w-[280px]'} rounded-lg border border-[#4E4E4E] p-3 ${styles.uploadContainer}`}>
            <div className='mb-8'>
              <h4 className='text-[14px] font-semibold text-[#FFFFFF]'>
                {currentPeriod} Leaderboard
              </h4>
            </div>
            <div className='flex items-center gap-2'>
              <ButtonIcon
                icon='file_download'
                isDark
                onClick={() => handleLeaderboardTemplateDownload()}
                size='medium'
                data-testid='download-template-btn'
                style={{
                  border: '1px solid #4E4E4E',
                  borderRadius: '4px',
                  padding: '4px',
                  marginRight: '8px'
                }}
              />

              {currentFile ? (
                <div className='fleSx-1 color-[#DADADA] flex items-center gap-2 overflow-hidden rounded bg-[#2a2a2a] py-1 pl-2'>
                  <span className='material-icons flex-shrink-0 text-[16px] text-[#0066FF]'>
                    description
                  </span>
                  <span
                    className='flex-1 truncate text-[14px] text-[#DADADA]'
                    title={currentFile.name}>
                    {currentFile.name}
                  </span>
                  <button
                    className={styles.refreshButton}
                    onClick={handleRefresh}
                    aria-label='Refresh'>
                    <span className='material-icons text-[16px]'>sync</span>
                  </button>
                </div>
              ) : (
                <Button
                  handleButton={handleOpenUpload}
                  uiSchema={{
                    title: 'Upload Template',
                    variant: 'primary',
                    size: 'medium',
                    isDark: true
                  }}
                />
              )}
            </div>
          </div>
          {currentLastUpdatedLabel && (
            <p className='mt-2 text-[12px] text-[#DADADA]'>
              {currentLastUpdatedLabel}
            </p>
          )}

          <LeaderboardTable data={currentData} period={currentPeriod} />
        </div>
      </div>

      <LeaderboardUploadModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onUpload={handleUpload}
        onDone={handleUploadDone}
        onDelete={handleFileDelete}
        existingFileName={currentFile?.name || ''}
        period={currentPeriod}
      />
    </>
  );
};

LeaderboardSection.propTypes = {
  formData: PropTypes.object.isRequired,
  handleFormChange: PropTypes.func.isRequired
};

export default React.memo(LeaderboardSection);
